import { useState, useEffect, useCallback, useMemo } from 'react';
import { Lease } from '../../types/leases';
import { BuildingSummary } from '../../types/protocols';
import { leasesApi } from '../../api/leasesApi';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { naturalCompare } from '../../Helpers/naturalSort';
import { LeaseMutations, useLeaseMutations } from './useLeaseMutations';

interface UseLeasesAdminProps {
    /** Extra reload after a mutation, e.g. the cached building, whose objects' currentLeaseKey the callable moved. */
    onMutated?: () => Promise<void>;
}

export interface UseLeasesAdminReturn extends LeaseMutations {
    leases: Lease[];
    loading: boolean;
    loadError: string | null;
    /** Every building, sorted by name, for the filter and the modal's Byggnad field. */
    buildings: BuildingSummary[];
    buildingName: (buildingKey: string) => string;
    refresh: () => Promise<void>;
}

/** Hyresavtal (/leases): every lease and the building names; leases reload after each mutation. */
export const useLeasesAdmin = ({ onMutated }: UseLeasesAdminProps = {}): UseLeasesAdminReturn => {
    const { buildings: summaries } = useBuildingsData();
    const [leases, setLeases] = useState<Lease[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const buildings = useMemo(
        () => [...(summaries ?? [])].sort((a, b) => naturalCompare(a.name || '', b.name || '')),
        [summaries]
    );
    const buildingName = useCallback(
        (buildingKey: string) => buildings.find(b => b.key === buildingKey)?.name || 'Okänd byggnad',
        [buildings]
    );

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            setLeases(await leasesApi.getAll());
        } catch (err) {
            console.error('Error loading leases:', err);
            setLoadError('Kunde inte ladda hyresavtal');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const afterMutation = useCallback(async () => {
        await load();
        if (onMutated) await onMutated();
    }, [load, onMutated]);

    const mutations = useLeaseMutations(afterMutation);

    return { leases, loading, loadError, buildings, buildingName, refresh: load, ...mutations };
};
